import React from 'react';
import { X, MapPin, Check } from 'lucide-react';
import AddressAutocomplete from '../AddressAutocomplete';

const GigModal = ({
  show,
  onClose,
  editingGig,
  setEditingGig,
  gigPlaylists,
  gigs,
  saveGig,
  deleteGig,
  savingGig,
  captureGPSLocation,
  capturingGPS
}) => {
  if (!show || !editingGig) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50"
      style={{ pointerEvents: 'none' }}
    >
      <div 
        data-testid="create-show-modal"
        className="rock-background gig-card border-2 border-orange max-w-2xl w-full h-[90vh] flex flex-col"
        style={{ pointerEvents: 'auto' }}
      >
        
        {/* HEADER - Fixed */}
        <div className="flex justify-between items-center p-6 pb-4 border-b border-white/10 flex-shrink-0">
          <h2 className="concert-heading text-3xl md:text-4xl text-orange">
            {gigs.find(g => g.id === editingGig.id) ? '🎸 EDIT SHOW' : '⚡ CREATE SHOW'}
          </h2>
          <button 
            onClick={() => {
              onClose();
            }} 
            className="text-white hover:text-red-400 p-2 touch-target"
          >
            <X size={32} />
          </button>
        </div>

        {/* BODY - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Venue Name */}
            <div>
              <label className="text-electric font-bold mb-2 block text-sm uppercase tracking-wider">
                Venue Name *
              </label>
              <input
                type="text"
                value={editingGig.venueName}
                onChange={(e) => setEditingGig({...editingGig, venueName: e.target.value})}
                className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none"
                placeholder="e.g. The Blue Note Jazz Club"
              />
            </div>
  
            {/* Venue Location Section */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="concert-heading text-xl text-neon flex items-center mb-4">
                <MapPin className="mr-2" size={24}/>
                VENUE LOCATION
              </h3>
              
              <div className="space-y-4">
                {/* GPS Capture Button */}
                <button
                  onClick={captureGPSLocation}
                  disabled={capturingGPS}
                  className="btn btn-electric w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {capturingGPS ? (
                    <>
                      <span className="loading-pulse">📍</span>
                      <span>Capturing...</span>
                    </>
                  ) : (
                    <>
                      <span>📍</span>
                      <span>Use My Current Location</span>
                    </>
                  )}
                </button>
  
                {/* Address Autocomplete */}
                <div>
                  <label className="block text-sm font-bold text-neon mb-2">
                    OR SEARCH ADDRESS:
                  </label>
                  {show && editingGig && (
                    <AddressAutocomplete
                      key={editingGig.id}
                      onSelect={(data) => {
                        console.log('✅ Address captured in GigModal:', data);
                        setEditingGig({
                          ...editingGig,
                          address: data.address,
                          location: data.location,
                          venueName: editingGig.venueName || data.name
                        });
                      }}
                      placeholder="Type venue address..."
                    />
                  )}
                </div>                
  
                {/* Display Coordinates if captured */}
                {editingGig.location && (
                  <p className="text-neon font-bold mt-4">
                    ✅ Location Captured
                  </p>
                )}
              </div>
            </div>
  
            {/* Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-electric font-bold mb-2 block text-sm uppercase tracking-wider">
                  Date *
                </label>
                <input
                  type="date"
                  value={editingGig.date}
                  onChange={(e) => {
                    console.log('📅 Date selected:', e.target.value);
                    setEditingGig({ ...editingGig, date: e.target.value });
                  }}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white border border-white/30 focus:border-electric focus:outline-none [color-scheme:dark]"
                  required
                />
              </div>
              <div>
                <label className="text-electric font-bold mb-2 block text-sm uppercase tracking-wider">
                  Time *
                </label>
                <input
                  type="time"
                  value={editingGig.time}
                  onChange={(e) => {
                    console.log('⏰ Time selected:', e.target.value);
                    setEditingGig({...editingGig, time: e.target.value});
                  }}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white border border-white/30 focus:border-electric focus:outline-none [color-scheme:dark]"
                  required
                />
              </div>
            </div>
  
            {/* Playlist Selector */}
            <div>
              <label className="text-electric font-bold mb-2 block text-sm uppercase tracking-wider">
                Assign Setlist (Optional)
              </label>
              <select
                value={editingGig.playlistId || ''}
                onChange={(e) => setEditingGig({
                  ...editingGig,
                  playlistId: e.target.value ? parseInt(e.target.value) : null
                })}
                className="w-full px-4 py-3 rounded-lg bg-white/10 text-white border border-electric/30 focus:border-electric focus:outline-none"
              >
                <option value="" className="bg-gray-900 text-white">
                  No setlist (use master playlist)
                </option>
                {gigPlaylists.map(p => (
                  <option key={p.id} value={p.id} className="bg-gray-900 text-white">
                    {p.name} ({p.songs.length} songs)
                  </option>
                ))}
              </select>
            </div>

            {/* Queue Size Setting */}
            <div>
              <label className="text-electric font-bold mb-2 block text-sm uppercase tracking-wider">
                Maximum Queue Size
              </label>
              <input
                data-testid="max-queue-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={editingGig.queueSize ?? 20}
                onChange={(e) => {
                  const value = e.target.value;
                  
                  if (value === '') {
                    setEditingGig({
                      ...editingGig,
                      queueSize: ''
                    });
                    return;
                  }
                  
                  if (!/^\d+$/.test(value)) {
                    return;
                  }
                  
                  const num = parseInt(value);
                  setEditingGig({
                    ...editingGig,
                    queueSize: num
                  });
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  
                  if (value === '' || isNaN(parseInt(value))) {
                    setEditingGig({
                      ...editingGig,
                      queueSize: 20
                    });
                    return;
                  }
                  
                  const num = parseInt(value);
                  
                  if (num < 20) {
                    setEditingGig({
                      ...editingGig,
                      queueSize: 20
                    });
                    alert('⚠️ Minimum queue size is 20 songs');
                  } else if (num > 50) {
                    setEditingGig({
                      ...editingGig,
                      queueSize: 50
                    });
                    alert('⚠️ Maximum queue size is 50 songs');
                  }
                }}
                placeholder="20-50"
                className="w-full px-4 py-3 rounded-lg bg-white/10 text-white border border-electric/30 focus:border-electric focus:outline-none"
              />
              
              {editingGig.queueSize !== '' && editingGig.queueSize < 20 && (
                <p className="text-red-400 text-sm mt-2 font-bold">
                  ⚠️ Minimum queue size is 20 songs
                </p>
              )}
              
              {editingGig.queueSize > 50 && (
                <p className="text-red-400 text-sm mt-2 font-bold">
                  ⚠️ Maximum queue size is 50 songs
                </p>
              )}
              
              {(!editingGig.queueSize || (editingGig.queueSize >= 20 && editingGig.queueSize <= 50)) && (
                <p className="text-gray-light text-sm mt-2">
                  💡 Type any number between 20-50 songs
                </p>
              )}
            </div>
          </div>
        </div>

        {/* FOOTER - Fixed */}
        <div className="p-6 pt-4 border-t border-white/10 flex-shrink-0">
          <div className="flex flex-col md:flex-row gap-3">
            <button
              data-testid="save-show-btn"
              onClick={saveGig}
              disabled={savingGig}
              className={`flex-1 btn text-lg ${savingGig ? 'btn-ghost opacity-50 cursor-not-allowed' : 'btn-neon'}`}
            >
              <Check size={20}/>
              <span>{savingGig ? 'Saving...' : 'Save Show'}</span>
            </button>
            <button
              onClick={() => {
                onClose();
              }}
              className="btn btn-ghost"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GigModal;
